import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import AutocompleteField from '@zr/components/Fields/AutocompleteField';
import { fetchLocationResults } from '@zr/utilities/api/locationField';

/**
 * A form field that fetches location autocomplete results of type = 'postal_code'
 * (results with a zip code) from the provided url
 */
export function PartnerRequiredLocationField({
  input,
  id,
  locationAutocompleteUrl,
  onChange,
  onSelect,
  value,
  shouldItemRender,
  forcedResults,
}) {
  const [items, setItems] = useState([]);

  const fetchResults = useCallback(async (val) => {
    // select_code filter to only return postal_code results (i.e. results with a zip code)
    const results = val ? await fetchLocationResults(locationAutocompleteUrl, val, 'p') : [];

    setItems(results);
  }, [locationAutocompleteUrl]);

  const handleChange = (event) => {
    const { value: val } = event.target;
    fetchResults(val);
    onChange(event);
  };

  // allow parent component to imperatively set results if needed
  useEffect(() => {
    setItems(forcedResults);
  }, [forcedResults]);

  return (
    <div className="PartnerRequiredLocationField">
      <AutocompleteField
        getItemValue={(item) => (`${item.postal_code} ${item.city}, ${item.admin1_code}`)}
        items={items}
        renderItem={(item, isHighlighted) => (
          <div className={`item ${isHighlighted ? 'item_highlighted' : ''}`} key={item.geonameid}>
            {`${item.postal_code} ${item.city}, ${item.admin1_code}`}
          </div>
        )}
        shouldItemRender={shouldItemRender}
        autoHighlight
        onChange={handleChange}
        onSelect={onSelect}
        renderMenu={(menuItems) => <div className="menu">{menuItems}</div>}
        selectOnBlur
        inputProps={{ ...input, id }}
        value={value}
        wrapperStyle={{}}
      />
    </div>
  );
}

PartnerRequiredLocationField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
  }).isRequired,
  id: PropTypes.string.isRequired,
  locationAutocompleteUrl: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  forcedResults: PropTypes.arrayOf(PropTypes.object),
  shouldItemRender: PropTypes.func,
};

PartnerRequiredLocationField.defaultProps = {
  forcedResults: [],
  shouldItemRender: () => true, // default is to render all items
};

export default PartnerRequiredLocationField;
